import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Pipe from '../../assets/icons/pipe.svg';
import { ReactComponent as SwitchOff } from '../../assets/icons/switch-off.svg';
import { ReactComponent as SwitchOn } from '../../assets/icons/switch-on.svg';
import Breadcrumb from '../../components/Breadcrumbs.component';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { ConfirmButton } from '../../components/buttons/ConfirmButton.component';
import DotWithLabel from '../../components/DotWithLabel.component';
import Layout from '../../components/Layout/Layout.component';
import LeavePromptRedirectionBlock from '../../components/Prompt/LeavePrompt/LeavePromptRedirectionBlock.component';
import ProtectedImg from '../../components/ProtectedImg/ProtectedImg.component';
import { zoomFactorValue } from '../../components/Solution/Preview.types';
import TooltipOne from '../../components/TooltipOne/TooltipOne.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { UserPermission } from '../../enum/userPermissions';
import usePermission from '../../hooks/usePermission';
import { getCommitteeLabel } from '../../models/committees.types';
import { ISolutionCard, TasksSolutionSheetStatusDetail } from '../../models/solutionCard.types';
import {
  getSolutionCardDetails,
  updateTasksSolutionSheetDetails,
  updateTasksSolutionSheetPageRotationAngle,
} from '../../services/images/images.service';
import EnvWindow from '../../utils/env';
import { Paths } from '../../utils/routerUrls';
import { zoomIn, zoomOut } from '../../utils/zoom';
import { PagesNavigator } from './../../components/PagesNavigator/PagesNavigator.component';
import PagesThumbnails from './../../components/PagesThumbnails/PagesThumbnails.component';
import { thumbNailPage } from './../../components/PagesThumbnails/PagesThumbnails.types';
import { EditDetails } from './EditDetails.component';
import ImageEditor from './ImageEditor.component';
import { getToggleButtonStyle, useStyles } from './SolutionCard.styles';
import { Prompt } from '../../components/Prompt/Prompt.component';
import { useImageDimensions } from './useImageDimensions';
import { useImageCssRotation } from './useImageCssRotation';

const displayMargin = 10;
const cardWidth = 500;

interface SolutionCardProps {
  cardId: string;
}

const SolutionCard: React.FC<SolutionCardProps> = ({ cardId }) => {
  const { t } = useTranslation();
  const params = useParams<SolutionCardProps>();
  if(cardId === undefined){
    cardId = params.cardId
  }
  const classes = useStyles();
  const history = useHistory();
  const { openAlert, showLoader } = useLayoutContext();
  const [solutionCard, setSolutionCard] = useState<ISolutionCard>();
  const [pages, setPages] = useState<thumbNailPage[]>([]);
  const [zoomFactor, setZoomFactor] = useState<number>(zoomFactorValue.screenWidth);
  const [rotateAngle, setRotateAngle] = useState<number>(0);
  const [lastSavedRotateAngle, setLastSavedRotateAngle] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, seTotalPages] = useState<number>(0);
  const [isSidebar, setIsSidebar] = useState<boolean>(true);
  const [isCardPreviewFullWith, setIsCardPreviewFullWith] = useState<boolean>(false);
  const [sidebarDisplayMode, setSidebarDisplayMode] = useState<'thumbnails' | 'details'>('thumbnails');
  const [detailsEditMode, setDetailsEditMode] = useState<boolean>(false);
  const [isDuringSavingPicture, setIsDuringSavingPicture] = useState<boolean>(false);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [needsPictureReload, setNeedsPictureReload] = useState<boolean>(false);
  const [needsThumbnailReload, setNeedsThumbnailReload] = useState<boolean>(false);
  const [canEditDetails] = useState<boolean>(
    usePermission(UserPermission.AdminTaskSolutionSheetUpdate) ||
      usePermission(UserPermission.AdminOkeTaskSolutionSheetUpdate),
  );
  const hasAdminImageUpdatePermission = usePermission(UserPermission.AdminImageUpdate);
  const [idChangeError, setIdChangeError] = useState<boolean>(false);

  const imgRef = useRef<HTMLImageElement>(null);
  const imgContenerRef = useRef<HTMLDivElement>(null);

  const [contenerHeight, setContenerHeight] = useState(0);
  const [contenerWidth, setContenerWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState<number | undefined>(undefined);
  const [imageWidth, setImageWidth] = useState<number | undefined>(undefined);

  const { displayWidth, displayHeight } = useImageDimensions({
    cardWidth,
    contenerHeight,
    contenerWidth,
    imageHeight: imageHeight || 0,
    imageWidth: imageWidth || 0,
    rotateAngle,
    zoomFactor,
  });

  const { rotatedImageDimensions, imageWrapperDimensions } = useImageCssRotation({
    displayWidth,
    displayHeight,
    contenerWidth,
    contenerHeight,
    rotateAngle,
    displayMargin,
  });

  useEffect(() => {
    if (imageLoaded) {
      setImageWidth(imgRef.current?.naturalWidth);
      setImageHeight(imgRef.current?.naturalHeight);
    }
  }, [imageLoaded]);

  const canUpdatePage = usePermission(UserPermission.AdminOkeTaskSolutionSheetPageUpdate);

  const getNormalizesRotateAngle = (angle: number): number => {
    if (angle < 0) {
      return angle + 360;
    } else if (angle >= 360) {
      return angle - 360;
    } else {
      return angle;
    }
  };

  const saveRotated = () => {
    setIsDuringSavingPicture(true);
    showLoader(true, 'Trwa zapisywanie zmian');
    updateTasksSolutionSheetPageRotationAngle(cardId, currentPage, rotateAngle)
      .then(
        () => {
          openAlert('success');
          setNeedsThumbnailReload(true);
          setLastSavedRotateAngle(rotateAngle);
        },
        () => {
          openAlert('error');
        },
      )
      .finally(() => {
        setIsDuringSavingPicture(false);
        showLoader(false);
      });
  };

  const handleZoomChange = (event: any) => {
    setZoomFactor(event.target.value);
  };

  const handleZoomIn = useCallback(() => {
    zoomIn(zoomFactor, cardWidth, imgRef, setZoomFactor);
  }, [zoomFactor, imgRef.current]);

  const handleZoomOut = useCallback(() => {
    zoomOut(zoomFactor, cardWidth, imgRef, setZoomFactor);
  }, [zoomFactor, imgRef.current]);

  const handleRotateLeft = () => {
    setRotateAngle(getNormalizesRotateAngle(rotateAngle - 90));
  };

  const handleRotateRight = () => {
    setRotateAngle(getNormalizesRotateAngle(rotateAngle + 90));
  };

  const handleSidebarVisibility = () => {
    setIsSidebar(!isSidebar);
    setIsCardPreviewFullWith(!isCardPreviewFullWith);
  };

  const getSolutionCard = () => {
    getSolutionCardDetails(cardId).then((result) => {
      const pages: thumbNailPage[] = [];
      result.pages.forEach((item) => {
        pages.push({
          id: item.pageId,
          caption: item.pageNumber,
          url: `${EnvWindow.REACT_APP_IMAGES_API_URL}/ExamSheetPageImage/${item.pageId}`,
          urlToUpdate: `${EnvWindow.REACT_APP_IMAGES_API_URL}/ExamSheetPageImage/${item.pageId}`,
        });
      });
      setSolutionCard(result);
      setPages(pages);
      seTotalPages(result.pages.length);
    });
  };

  useEffect(() => {
    getSolutionCard();
  }, []);

  const handlePictureReload = useCallback(() => {
    setNeedsPictureReload(false);
    setRotateAngle(0);
    setLastSavedRotateAngle(0);
    setImageLoaded(true);
  }, []);

  const handleThumbnailReload = useCallback(() => {
    setNeedsThumbnailReload(false);
  }, []);

  const handleSidebarModeChange = (
    event: React.MouseEvent<HTMLElement>,
    selectedDisplayMode: 'thumbnails' | 'details' | null,
  ) => {
    if (selectedDisplayMode !== null) {
      setSidebarDisplayMode(selectedDisplayMode);
      setDetailsEditMode(false);
    }
  };

  const handleSave = (newExamineeIdentifier: string) => {
    updateTasksSolutionSheetDetails(cardId, newExamineeIdentifier)
      .then((result) => {
        if (result.data.isChangeSaved) {
          openAlert('success');
          if (solutionCard)
            setSolutionCard({
              ...solutionCard,
              examineeIdentifier: result.data.examineeIdentifier ?? '',
              statusId: result.data.statusId,
            });
          else getSolutionCard();
          setDetailsEditMode(false);
        } else {
          openAlert('error');
        }
      })
      .catch((error) => {
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        } else if (error.response.status === 400) {
          setIdChangeError(true);
        } else {
          openAlert('error');
        }
      })
      .finally(() => showLoader(false));
  };

  const isDraggingRef = useRef(false);
  const lastXRef = useRef(0);
  const lastYRef = useRef(0);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (
      event.currentTarget.scrollHeight > event.currentTarget.clientHeight ||
      event.currentTarget.scrollWidth > event.currentTarget.clientWidth
    ) {
      isDraggingRef.current = true;
      lastXRef.current = event.clientX;
      lastYRef.current = event.clientY;
      event.preventDefault();
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (isDraggingRef.current) {
      const deltaX = event.clientX - lastXRef.current;
      const deltaY = event.clientY - lastYRef.current;
      event.currentTarget.scrollTop = event.currentTarget.scrollTop - deltaY;
      event.currentTarget.scrollLeft = event.currentTarget.scrollLeft - deltaX;
      lastXRef.current = event.clientX;
      lastYRef.current = event.clientY;
    }
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    isDraggingRef.current = false;
  };

  const handleImageInit = useCallback(() => setImageLoaded(false), []);

  const updateContenerDimensions = () => {
    setContenerHeight((imgContenerRef.current?.clientHeight || 0) - 2 * displayMargin);
    setContenerWidth((imgContenerRef.current?.clientWidth || 0) - 2 * displayMargin);
  };

  useEffect(() => {
    if (!imgContenerRef.current) return;
    const observer = new ResizeObserver(() => {
      updateContenerDimensions();
    });
    observer.observe(imgContenerRef.current);
    return () => {
      if (imgContenerRef.current) {
        observer?.unobserve(imgContenerRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className={classes.main}>
        {hasAdminImageUpdatePermission && (
          <div className={classes.buttonsContainer}>
            <ConfirmButton
              onClick={saveRotated}
              disabled={rotateAngle === lastSavedRotateAngle}
              className={classes.saveButton}
            >
              Zapisz zmiany
            </ConfirmButton>
          </div>
        )}
        <div className={classes.container}>
          <div className={classes.cardContainer}>
            <TooltipOne title={isCardPreviewFullWith ? 'Rozwiń menu karty' : 'Zwiń menu karty'} placement="top">
              <ButtonOne
                className={`${classes.switchIconButton} ${
                  isCardPreviewFullWith ? classes.switchIconButtonFullWidth : classes.switchIconButtonDefaultWidth
                }`}
              >
                {isCardPreviewFullWith ? (
                  <SwitchOff onClick={handleSidebarVisibility} className={classes.switchIcon} />
                ) : (
                  <SwitchOn onClick={handleSidebarVisibility} className={classes.switchIcon} />
                )}
              </ButtonOne>
            </TooltipOne>
            <div
              className={`${classes.cardPreview} ${
                isCardPreviewFullWith ? classes.cardPreviewFullWidth : classes.cardPreviewDefaultWidth
              }`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              ref={imgContenerRef}
            >
              {solutionCard && pages.length && (
                <div
                  style={{
                    position: 'absolute',
                    ...imageWrapperDimensions,
                  }}
                >
                  <ProtectedImg
                    imgRef={imgRef}
                    alt={`Strona ${currentPage} karty rozwiązania zadania`}
                    style={{
                      ...rotatedImageDimensions,
                      transform: `rotate(${rotateAngle}deg)`,
                      opacity: pages[currentPage - 1].uploadInProgress ? '0.7' : '1',
                      position: 'absolute',
                      transformOrigin: 'left top',
                    }}
                    className={classes.card}
                    src={pages[currentPage - 1].url}
                    imgData={
                      pages[currentPage - 1].newImgData
                        ? pages[currentPage - 1].newImgData
                        : pages[currentPage - 1].imgData
                    }
                    forceReload={needsPictureReload}
                    onInit={handleImageInit}
                    onLoad={handlePictureReload}
                    uploadInProgress={pages[currentPage - 1].uploadInProgress}
                  />
                </div>
              )}
            </div>

            <div className={classes.cardHeader}>
              <div
                className={`${classes.cardPreviewMenu} ${
                  isCardPreviewFullWith ? classes.cardHeaderFullWidth : classes.cardHeaderDefaultWidth
                }`}
                style={{ left: isCardPreviewFullWith && hasAdminImageUpdatePermission ? 'calc(100% - 670px)' : '' }}
              >
                <PagesNavigator totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                <img className={classes.pipe} src={Pipe} alt="linia pionowa" />
                <ImageEditor
                  zoomFactor={zoomFactor}
                  cardPreviewMenuIconClass={classes.cardPreviewMenuIcon}
                  handleZoomIn={handleZoomIn}
                  handleZoomOut={handleZoomOut}
                  handleZoomChange={handleZoomChange}
                  handleRotateLeft={handleRotateLeft}
                  handleRotateRight={handleRotateRight}
                  saving={isDuringSavingPicture}
                />
              </div>
            </div>
            {isSidebar && (
              <div className={classes.cardSidebar}>
                <div className={classes.cardSidebarMenu}>
                  <ToggleButtonGroup
                    value={sidebarDisplayMode}
                    size="small"
                    color="primary"
                    exclusive
                    onChange={handleSidebarModeChange}
                    fullWidth
                    aria-label="wybór trybu wyświetlania menu bocznego"
                  >
                    <ToggleButton
                      size="small"
                      style={getToggleButtonStyle(sidebarDisplayMode === 'thumbnails')}
                      value="thumbnails"
                      disableRipple
                    >
                      Miniatury
                    </ToggleButton>
                    <ToggleButton
                      size="small"
                      style={getToggleButtonStyle(sidebarDisplayMode === 'details')}
                      value="details"
                      disableRipple
                    >
                      Szczegóły
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
                {sidebarDisplayMode === 'thumbnails' && (
                  <div className={classes.cardThumbnails}>
                    <PagesThumbnails
                      currentPageNo={currentPage}
                      thumbnailWidth="200px"
                      pages={pages}
                      onClickPage={(pageNo: number) => setCurrentPage(pageNo)}
                      needsThumbnailReload={needsThumbnailReload}
                      onThumbnailReload={handleThumbnailReload}
                      actions={canUpdatePage}
                      onFileValidated={(file: Blob, index: number) => {
                        setPages(pages.map((page, i) => (i === index ? { ...page, newImgData: file } : page)));
                      }}
                      onFileReplacementCancel={(index: number) => {
                        setPages(
                          pages.map((page, i) =>
                            i === index ? { ...page, newImgData: undefined, uploadInProgress: false } : page,
                          ),
                        );
                      }}
                      onUploadStarted={(index: number) => {
                        setPages(pages.map((page, i) => (i === index ? { ...page, uploadInProgress: true } : page)));
                      }}
                      onFileSaved={(index: number) => {
                        setPages(
                          pages.map((page, i) =>
                            i === index
                              ? { ...page, imgData: page.newImgData, newImgData: undefined, uploadInProgress: false }
                              : page,
                          ),
                        );
                      }}
                    />
                  </div>
                )}
                {sidebarDisplayMode === 'details' &&
                  solutionCard &&
                  (detailsEditMode ? (
                    <EditDetails
                      examineeIdentifier={solutionCard.examineeIdentifier}
                      onCancel={() => setDetailsEditMode(false)}
                      onSave={handleSave}
                    />
                  ) : (
                    <ul className={classes.cardDetails}>
                      <li>
                        Status:
                        <span className={classes.cardDetail}>
                          <DotWithLabel
                            color={TasksSolutionSheetStatusDetail(solutionCard.statusId).color}
                            label={TasksSolutionSheetStatusDetail(solutionCard.statusId).label}
                          />
                        </span>
                      </li>
                      <li>
                        ID karty: <span className={classes.cardDetail}>{cardId}</span>
                      </li>
                      <li>
                        Numer wiązki: <span className={classes.cardDetail}>{solutionCard.bundleNumber}</span>
                      </li>
                      <TooltipOne title={`Nazwa wiązki: ${solutionCard.bundleName}`} placement="top">
                        <li>
                          Nazwa wiązki: <span className={classes.cardDetail}>{solutionCard.bundleName}</span>
                        </li>
                      </TooltipOne>
                      <li>
                        Kod arkusza: <span className={classes.cardDetail}>{solutionCard.sheetCode}</span>
                      </li>
                      <li>
                        Komisja:{' '}
                        <span className={classes.cardDetail}>{getCommitteeLabel(solutionCard.examCommitteeId)}</span>
                      </li>
                      <li>
                        ID szkoły: <span className={classes.cardDetail}>{solutionCard.schoolCode}</span>
                      </li>
                      <li>
                        ID zdającego: <span className={classes.cardDetail}>{solutionCard.examineeIdentifier}</span>
                      </li>
                    </ul>
                  ))}
                {canEditDetails && sidebarDisplayMode === 'details' && !detailsEditMode && (
                  <ButtonOne
                    className={classes.editDetailsButton}
                    variant="outlined"
                    color="primary"
                    onClick={() => setDetailsEditMode(!detailsEditMode)}
                  >
                    EDYTUJ DANE
                  </ButtonOne>
                )}
              </div>
            )}
          </div>
        </div>
        <Prompt
          open={idChangeError}
          type={'warning'}
          mainText={'Brak zdającego'}
          subTexts={[
            {
              text: 'Zmiana ID zdającego na karcie przetworzonej, wymaga podania Id istniejącego w systemie',
            },
          ]}
          mainActionButtonText="OK"
          onConfirm={() => setIdChangeError(false)}
        />
        <LeavePromptRedirectionBlock
          history={history}
          blockingCondition={rotateAngle !== lastSavedRotateAngle && hasAdminImageUpdatePermission}
        />
      </div>
    </>
  );
};

export default SolutionCard;
